import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Folder,
  File,
  ChevronRight,
  MoreVertical,
  FolderPlus,
  Edit2,
  Trash2,
  Wand2,
  ArrowLeft,
  RefreshCw,
  ChevronDown,
} from 'lucide-react'
import { libraries, files, type FileEntry, type RenamePreview } from '../api/client'
import clsx from 'clsx'

function formatFileSize(bytes: number | null): string {
  if (bytes === null) return '-'
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

export default function FileManager() {
  const queryClient = useQueryClient()
  const [selectedLibrary, setSelectedLibrary] = useState<string | null>(null)
  const [currentPath, setCurrentPath] = useState<string>('')
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; item: FileEntry } | null>(null)

  // Modal states
  const [showNewFolderModal, setShowNewFolderModal] = useState(false)
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showAutoRenameModal, setShowAutoRenameModal] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [renameValue, setRenameValue] = useState('')
  const [itemToRename, setItemToRename] = useState<FileEntry | null>(null)
  const [itemToDelete, setItemToDelete] = useState<FileEntry | null>(null)
  const [autoRenamePreview, setAutoRenamePreview] = useState<RenamePreview[]>([])
  const [autoRenamePath, setAutoRenamePath] = useState<string>('')

  // Fetch libraries
  const { data: libraryList } = useQuery({
    queryKey: ['libraries'],
    queryFn: () => libraries.list(),
  })

  // Fetch directory contents
  const { data: directory, isLoading, refetch } = useQuery({
    queryKey: ['files', selectedLibrary, currentPath],
    queryFn: () => files.listDirectory(selectedLibrary!, currentPath),
    enabled: !!selectedLibrary,
  })

  // Mutations
  const createFolderMutation = useMutation({
    mutationFn: ({ name }: { name: string }) =>
      files.createFolder(selectedLibrary!, currentPath, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files', selectedLibrary] })
      setShowNewFolderModal(false)
      setNewFolderName('')
    },
  })

  const renameMutation = useMutation({
    mutationFn: ({ path, newName }: { path: string; newName: string }) =>
      files.rename(selectedLibrary!, path, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files', selectedLibrary] })
      setShowRenameModal(false)
      setItemToRename(null)
      setRenameValue('')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: ({ path }: { path: string }) => files.delete(selectedLibrary!, path),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files', selectedLibrary] })
      setShowDeleteModal(false)
      setItemToDelete(null)
    },
  })

  const autoRenameMutation = useMutation({
    mutationFn: ({ path, recursive }: { path: string; recursive: boolean }) =>
      files.applyAutoRename(selectedLibrary!, path, recursive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files', selectedLibrary] })
      setShowAutoRenameModal(false)
      setAutoRenamePreview([])
    },
  })

  const handleNavigate = (entry: FileEntry) => {
    if (entry.is_directory) {
      setCurrentPath(entry.path)
      setSelectedItems(new Set())
    }
  }

  const handleGoUp = () => {
    if (directory?.parent !== undefined) {
      setCurrentPath(directory.parent || '')
      setSelectedItems(new Set())
    }
  }

  const handleContextMenu = (e: React.MouseEvent, item: FileEntry) => {
    e.preventDefault()
    setContextMenu({ x: e.clientX, y: e.clientY, item })
  }

  const handleRename = (item: FileEntry) => {
    setItemToRename(item)
    setRenameValue(item.name)
    setShowRenameModal(true)
    setContextMenu(null)
  }

  const handleDelete = (item: FileEntry) => {
    setItemToDelete(item)
    setShowDeleteModal(true)
    setContextMenu(null)
  }

  const handleAutoRename = async (path: string) => {
    if (!selectedLibrary) return
    try {
      const preview = await files.previewAutoRename(selectedLibrary, path, true)
      setAutoRenamePreview(preview.changes)
      setAutoRenamePath(path)
      setShowAutoRenameModal(true)
    } catch (error) {
      console.error('Failed to preview auto-rename:', error)
    }
    setContextMenu(null)
  }

  const handleSelect = (item: FileEntry, e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      const newSelected = new Set(selectedItems)
      if (newSelected.has(item.path)) {
        newSelected.delete(item.path)
      } else {
        newSelected.add(item.path)
      }
      setSelectedItems(newSelected)
    } else {
      setSelectedItems(new Set([item.path]))
    }
  }

  // Close context menu on click outside
  const handleClickOutside = () => {
    setContextMenu(null)
  }

  // Breadcrumbs
  const pathParts = currentPath.split('/').filter(Boolean)
  const breadcrumbs = [
    { name: directory?.library_name || 'Root', path: '' },
    ...pathParts.map((part, index) => ({
      name: part,
      path: pathParts.slice(0, index + 1).join('/'),
    })),
  ]

  return (
    <div className="p-4 sm:p-6" onClick={handleClickOutside}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">File Manager</h1>
        <select
          value={selectedLibrary || ''}
          onChange={(e) => {
            setSelectedLibrary(e.target.value || null)
            setCurrentPath('')
          }}
          className="bg-dark-800 border border-dark-700 rounded-lg px-3 py-2 text-sm sm:text-base text-dark-100"
        >
          <option value="">Select Library</option>
          {libraryList?.map((lib) => (
            <option key={lib.id} value={lib.id}>
              {lib.name} ({lib.library_type})
            </option>
          ))}
        </select>
      </div>

      {selectedLibrary && directory && (
        <>
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button
              onClick={handleGoUp}
              disabled={!directory.parent && currentPath === ''}
              className="btn btn-secondary flex items-center gap-1.5 text-sm"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">Back</span>
            </button>
            <button
              onClick={() => setShowNewFolderModal(true)}
              className="btn btn-secondary flex items-center gap-1.5 text-sm"
            >
              <FolderPlus size={16} />
              <span className="hidden sm:inline">New Folder</span>
            </button>
            <button
              onClick={() => handleAutoRename(currentPath)}
              className="btn btn-secondary flex items-center gap-1.5 text-sm"
            >
              <Wand2 size={16} />
              <span className="hidden sm:inline">Auto-Rename</span>
            </button>
            <button
              onClick={() => refetch()}
              className="btn btn-secondary flex items-center gap-1.5 text-sm"
            >
              <RefreshCw size={16} />
            </button>
          </div>

          {/* Breadcrumbs */}
          <div className="flex items-center gap-1 mb-4 text-xs sm:text-sm text-dark-400 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            {breadcrumbs.map((crumb, index) => (
              <div key={crumb.path} className="flex items-center">
                {index > 0 && <ChevronRight size={14} className="mx-1" />}
                <button
                  onClick={() => setCurrentPath(crumb.path)}
                  className={clsx(
                    'hover:text-dark-100 transition-colors whitespace-nowrap',
                    index === breadcrumbs.length - 1 && 'text-dark-100 font-medium'
                  )}
                >
                  {crumb.name}
                </button>
              </div>
            ))}
          </div>

          {/* File list */}
          <div className="card p-0 overflow-hidden">
            {isLoading ? (
              <div className="p-8 text-center text-dark-400">Loading...</div>
            ) : directory.entries.length === 0 ? (
              <div className="p-8 text-center text-dark-400">This folder is empty</div>
            ) : (
              <>
                {/* Desktop table */}
                <table className="w-full hidden sm:table">
                  <thead>
                    <tr className="text-left text-dark-400 text-sm border-b border-dark-700">
                      <th className="py-3 pl-4">Name</th>
                      <th className="py-3">Size</th>
                      <th className="py-3 hidden md:table-cell">Modified</th>
                      <th className="py-3 pr-4 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {directory.entries.map((entry) => (
                      <tr
                        key={entry.path}
                        className={clsx(
                          'group hover:bg-dark-700/50 cursor-pointer',
                          selectedItems.has(entry.path) && 'bg-primary-900/30'
                        )}
                        onClick={(e) => handleSelect(entry, e)}
                        onDoubleClick={() => handleNavigate(entry)}
                        onContextMenu={(e) => handleContextMenu(e, entry)}
                      >
                        <td className="py-3 pl-4">
                          <div className="flex items-center gap-3">
                            {entry.is_directory ? (
                              <Folder size={20} className="text-yellow-500" />
                            ) : (
                              <File size={20} className="text-dark-400" />
                            )}
                            <span className="font-medium truncate">{entry.name}</span>
                          </div>
                        </td>
                        <td className="py-3 text-dark-400 text-sm">
                          {entry.is_directory ? '-' : formatFileSize(entry.size)}
                        </td>
                        <td className="py-3 text-dark-400 text-sm hidden md:table-cell">
                          {entry.modified || '-'}
                        </td>
                        <td className="py-3 pr-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleContextMenu(e, entry)
                            }}
                            className="p-1 rounded hover:bg-dark-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreVertical size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile list */}
                <div className="sm:hidden divide-y divide-dark-700">
                  {directory.entries.map((entry) => (
                    <div
                      key={entry.path}
                      className={clsx(
                        'flex items-center gap-3 p-3 cursor-pointer',
                        selectedItems.has(entry.path) ? 'bg-primary-900/30' : 'hover:bg-dark-700/50'
                      )}
                      onClick={(e) => handleSelect(entry, e)}
                      onDoubleClick={() => handleNavigate(entry)}
                    >
                      {entry.is_directory ? (
                        <Folder size={20} className="text-yellow-500 flex-shrink-0" />
                      ) : (
                        <File size={20} className="text-dark-400 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-sm">{entry.name}</p>
                        <p className="text-xs text-dark-400">
                          {entry.is_directory ? 'Folder' : formatFileSize(entry.size)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleContextMenu(e, entry)
                        }}
                        className="p-1.5 rounded hover:bg-dark-600"
                      >
                        <MoreVertical size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {!selectedLibrary && (
        <div className="card text-center py-12">
          <p className="text-dark-400 mb-2">Select a library to browse its files</p>
          <p className="text-dark-500 text-sm">
            You can manage files, rename them, and organize your media
          </p>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-dark-800 border border-dark-700 rounded-lg shadow-xl py-1 z-50"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => handleRename(contextMenu.item)}
            className="w-full px-4 py-2 text-left text-sm hover:bg-dark-700 flex items-center gap-2"
          >
            <Edit2 size={14} />
            Rename
          </button>
          <button
            onClick={() => handleAutoRename(contextMenu.item.path)}
            className="w-full px-4 py-2 text-left text-sm hover:bg-dark-700 flex items-center gap-2"
          >
            <Wand2 size={14} />
            Auto-Rename
          </button>
          <button
            onClick={() => handleDelete(contextMenu.item)}
            className="w-full px-4 py-2 text-left text-sm hover:bg-dark-700 flex items-center gap-2 text-red-400"
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      )}

      {/* New Folder Modal */}
      {showNewFolderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-lg p-4 sm:p-6 w-full max-w-sm">
            <h2 className="text-xl font-bold mb-4">New Folder</h2>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-2 mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowNewFolderModal(false)
                  setNewFolderName('')
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => createFolderMutation.mutate({ name: newFolderName })}
                disabled={!newFolderName || createFolderMutation.isPending}
                className="btn btn-primary"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {showRenameModal && itemToRename && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-lg p-4 sm:p-6 w-full max-w-sm">
            <h2 className="text-xl font-bold mb-4">Rename</h2>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="New name"
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-2 mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowRenameModal(false)
                  setItemToRename(null)
                  setRenameValue('')
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  renameMutation.mutate({ path: itemToRename.path, newName: renameValue })
                }
                disabled={!renameValue || renameValue === itemToRename.name || renameMutation.isPending}
                className="btn btn-primary"
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-lg p-4 sm:p-6 w-full max-w-sm">
            <h2 className="text-xl font-bold mb-4">Delete</h2>
            <p className="text-dark-300 mb-4">
              Are you sure you want to delete{' '}
              <span className="font-medium text-dark-100">{itemToDelete.name}</span>?
              {itemToDelete.is_directory && ' This will delete all contents.'}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setItemToDelete(null)
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate({ path: itemToDelete.path })}
                disabled={deleteMutation.isPending}
                className="btn bg-red-600 hover:bg-red-700 text-white"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-Rename Preview Modal */}
      {showAutoRenameModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[85vh] flex flex-col">
            <h2 className="text-xl font-bold mb-4">Auto-Rename Preview</h2>
            {autoRenamePreview.length === 0 ? (
              <p className="text-dark-400 mb-4">No changes to apply. Files are already organized correctly.</p>
            ) : (
              <>
                <p className="text-dark-400 mb-4">
                  The following {autoRenamePreview.length} file(s) will be renamed:
                </p>
                <div className="flex-1 overflow-y-auto mb-4 space-y-2">
                  {autoRenamePreview.map((change, index) => (
                    <div key={index} className="bg-dark-700 rounded-lg p-3 text-sm">
                      <div className="flex items-center gap-2 text-dark-400">
                        <span className="truncate">{change.original_path}</span>
                      </div>
                      <div className="flex items-center gap-2 text-green-400 mt-1">
                        <ChevronDown size={14} />
                        <span className="truncate">{change.new_path}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowAutoRenameModal(false)
                  setAutoRenamePreview([])
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              {autoRenamePreview.length > 0 && (
                <button
                  onClick={() =>
                    autoRenameMutation.mutate({ path: autoRenamePath, recursive: true })
                  }
                  disabled={autoRenameMutation.isPending}
                  className="btn btn-primary"
                >
                  Apply Changes
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
